import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Replay from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FeatureFlagKey,
  FeatureFlagMeta,
  FeatureFlagType,
  getFeatureFlagMeta,
  KNOWN_FEATURE_FLAG_KEYS,
} from "encamp-shared/src/featureFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import EncampLogo from "../../assets/EncampLogo.svg";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";

export type FlagState = boolean | "mixed";

/**
 * The value a flag will be set to when the pending changes are committed.
 * Undefined means "leave alone" — for multi-tenant bulk edits, each tenant
 * keeps its current value.
 */
export type PendingValue = boolean | undefined;

export type FeatureFlagTogglePanelProps = {
  /**
   * "single" renders a binary `Switch` per flag and calls `onChange`
   * immediately when toggled. "multi" renders an On/Off toggle-button
   * group (with neither button selected when the underlying tenants
   * have a mixed value, plus a Revert icon to clear pending changes)
   * and expects the caller to buffer pending
   * changes and submit them in bulk.
   */
  mode: "single" | "multi";
  /**
   * Current state per flag. In single mode values are always boolean; in
   * multi mode "mixed" is valid (meaning selected tenants have a mix of
   * true/false values).
   */
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>;
  /**
   * Staged changes, keyed by flag. Only keys the user has explicitly
   * interacted with should appear here. Used in multi mode.
   */
  pendingChanges?: Partial<Record<FeatureFlagKey, PendingValue>>;
  /**
   * Fires when the user toggles a flag. In single mode `nextValue` is
   * always boolean. In multi mode `undefined` signals the user has
   * reverted the flag to its original ("mixed" or unchanged) state.
   */
  onChange: (key: FeatureFlagKey, nextValue: PendingValue) => void;
  disabled?: boolean;
};

type FlagNode = {
  key: FeatureFlagKey;
  children: FlagNode[];
};

// -----------------------------------------------------------------------
// Expand / collapse persistence
// -----------------------------------------------------------------------
// A single localStorage entry holds expand/collapse state for every flag
// that has children, keyed by `FeatureFlagKey`. Every instance of the
// panel reads from and writes to the same key, so a user who collapses
// "Tier II module" on the single-tenant tab will still see it collapsed
// the next time they open the staff bulk drawer, and vice versa.
//
// We route through `useLocalStorageState` (which wraps
// `use-local-storage-state`) so the parsed value is cached once globally
// per key — N FlagRow instances share a single read + a single
// subscription, instead of each one parsing the same JSON blob in its
// own `useState` initializer (twice in StrictMode).
const EXPANDED_STORAGE_KEY = "featureFlagPanel:expanded";
const EMPTY_EXPANDED_MAP: Record<string, boolean> = {};

function useFlagExpanded(flagKey: FeatureFlagKey): [boolean, () => void] {
  const [expandedMap, setExpandedMap] = useLocalStorageState<Record<string, boolean>>(
    EXPANDED_STORAGE_KEY,
    { defaultValue: EMPTY_EXPANDED_MAP }
  );

  // Default to expanded for any flag we haven't recorded a preference
  // for — the panel was useless when collapsed by default in user
  // testing, so we lean toward "show me everything".
  const expanded = expandedMap[flagKey] ?? true;

  const toggle = useCallback(() => {
    setExpandedMap((prev) => ({
      ...prev,
      [flagKey]: !(prev[flagKey] ?? true),
    }));
  }, [flagKey, setExpandedMap]);

  return [expanded, toggle];
}

// -----------------------------------------------------------------------
// Dependency cascade
// -----------------------------------------------------------------------
/**
 * Given a flag the user is turning on, return the additional flag keys
 * that must also be turned on so the newly-enabled flag actually
 * functions (parents in the hierarchy plus anything in its `requires`
 * list, transitively). Keys that `isEffectivelyOn` already reports as
 * on are excluded.
 *
 * Dependencies with `staffOverride: true` are also excluded: those flags
 * are always-on for staff regardless of stored value, so cascading them
 * on would silently flip the customer-facing flag too — usually not what
 * the staff user is trying to do when they toggle a child or required
 * flag for dogfooding. The {@link DependencyHint} component surfaces the
 * resulting "active for staff only — customer needs X" state instead.
 *
 * Intentionally one-directional on activation. Deactivation cascades
 * children + dependents off (see {@link collectDependencyKeysToDeactivate})
 * — but we only run that flow when the user explicitly toggles a flag
 * off, so they always see the cascade happen.
 */
export function collectDependencyKeysToActivate(
  startKey: FeatureFlagKey,
  isEffectivelyOn: (key: FeatureFlagKey) => boolean
): FeatureFlagKey[] {
  const queue: FeatureFlagKey[] = [];
  const visited = new Set<FeatureFlagKey>([startKey]);
  const result: FeatureFlagKey[] = [];

  const pushDeps = (key: FeatureFlagKey) => {
    const m = getFeatureFlagMeta(key);
    if (m.parentKey) queue.push(m.parentKey);
    for (const r of m.requires) queue.push(r);
  };

  pushDeps(startKey);

  while (queue.length > 0) {
    const k = queue.shift();
    // The `length > 0` guard above means shift() can't actually return
    // undefined here, but the typing reflects the general signature.
    // Falling through with `break` is the typed-safe equivalent of `!`
    // and avoids the AGENTS.md non-null-assertion rule.
    if (k === undefined) break;
    if (visited.has(k)) continue;
    visited.add(k);
    if (isEffectivelyOn(k) || getFeatureFlagMeta(k).staffOverride) continue;
    result.push(k);
    pushDeps(k);
  }

  return result;
}

/**
 * Inverse of {@link collectDependencyKeysToActivate}: given a flag the
 * user is turning off, return the additional flag keys that must also
 * be turned off so we never leave a dependent flag in the "inactive —
 * needs …" state.
 *
 * Two classes of dependents are cascaded off:
 *   1. Child flags (any flag whose `parentKey` is reachable from
 *      `startKey`) — turning off a module implies its add-ons also turn
 *      off.
 *   2. Flags that list `startKey` in their `requires` — turning off a
 *      required flag implies any flag that needs it also turns off.
 *
 * Traversal is transitive on both edges, so turning off "Waste module"
 * also turns off "Waste manifest compliance" (which requires
 * "Waste manifest discrepancies", which is a child of Waste module).
 * Keys that `isEffectivelyOn` already reports as off are excluded.
 *
 * If `startKey` itself has `staffOverride: true`, no cascade runs: the
 * flag remains effectively-on for staff after the user toggles it off
 * (it's the customer-facing stored value that flipped, not the staff
 * view), so dependents are still functional for staff and shouldn't be
 * dragged off. The customer-facing inconsistency is surfaced through the
 * {@link DependencyHint} "active for staff only" chip on the dependents.
 */
export function collectDependencyKeysToDeactivate(
  startKey: FeatureFlagKey,
  isEffectivelyOn: (key: FeatureFlagKey) => boolean
): FeatureFlagKey[] {
  if (getFeatureFlagMeta(startKey).staffOverride) return [];

  const queue: FeatureFlagKey[] = [];
  const visited = new Set<FeatureFlagKey>([startKey]);
  const result: FeatureFlagKey[] = [];

  const pushDependents = (key: FeatureFlagKey) => {
    for (const otherKey of KNOWN_FEATURE_FLAG_KEYS) {
      const meta = getFeatureFlagMeta(otherKey);
      if (meta.parentKey === key) {
        queue.push(otherKey);
        continue;
      }
      if (meta.requires.includes(key)) {
        queue.push(otherKey);
      }
    }
  };

  pushDependents(startKey);

  while (queue.length > 0) {
    const k = queue.shift();
    // See comment in `collectDependencyKeysToActivate` — guarded read
    // instead of `!` to satisfy the no-non-null-assertion rule.
    if (k === undefined) break;
    if (visited.has(k)) continue;
    visited.add(k);
    if (!isEffectivelyOn(k)) continue;
    result.push(k);
    pushDependents(k);
  }

  return result;
}

// Alphabetical by the human label, locale-aware so e.g. "Émissions" sorts
// next to "Emissions". Using the label (not the key) so the visible order
// matches what users scan for.
const byLabel = (a: FlagNode, b: FlagNode) =>
  getFeatureFlagMeta(a.key).label.localeCompare(getFeatureFlagMeta(b.key).label, undefined, {
    sensitivity: "base",
  });

function buildFlagTree(): {
  moduleFlags: FlagNode[];
  otherFlags: FlagNode[];
} {
  const childrenByParent = new Map<FeatureFlagKey, FlagNode[]>();
  const moduleFlags: FlagNode[] = [];
  const otherFlags: FlagNode[] = [];

  for (const key of KNOWN_FEATURE_FLAG_KEYS) {
    const meta = getFeatureFlagMeta(key);
    const node: FlagNode = { key, children: [] };
    if (meta.parentKey) {
      const siblings = childrenByParent.get(meta.parentKey) ?? [];
      siblings.push(node);
      childrenByParent.set(meta.parentKey, siblings);
      continue;
    }
    if (meta.type === FeatureFlagType.Module) {
      moduleFlags.push(node);
    } else {
      // Everything top-level that isn't a Module — type `Other` plus any
      // defensive AddOn entry that's missing a parent (shouldn't happen,
      // but we'd rather still render it than drop it silently).
      otherFlags.push(node);
    }
  }

  for (const module of moduleFlags) {
    module.children = (childrenByParent.get(module.key) ?? []).slice().sort(byLabel);
  }

  moduleFlags.sort(byLabel);
  otherFlags.sort(byLabel);

  return { moduleFlags, otherFlags };
}

export function FeatureFlagTogglePanel(props: FeatureFlagTogglePanelProps) {
  const { moduleFlags, otherFlags } = useMemo(buildFlagTree, []);

  return (
    <Stack spacing={3}>
      {moduleFlags.length > 0 && (
        <FlagSection title="Product modules">
          {moduleFlags.map((node) => (
            <FlagRow key={node.key} node={node} {...props} />
          ))}
        </FlagSection>
      )}
      {otherFlags.length > 0 && (
        <FlagSection title="Other">
          {otherFlags.map((node) => (
            <FlagRow key={node.key} node={node} {...props} />
          ))}
        </FlagSection>
      )}
    </Stack>
  );
}

function FlagSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {/* Generous spacing between top-level module rows — the sub-flag
          tree rail, chips, and description can make each row tall, and
          tighter spacing runs them together visually. */}
      <Stack spacing={3}>{children}</Stack>
    </Box>
  );
}

type FlagRowProps = FeatureFlagTogglePanelProps & { node: FlagNode; depth?: number };

// The parent module's toggle (a medium MUI Switch in single mode, an
// On/Off ToggleButtonGroup in multi mode) is the visual anchor for the
// children rail. Every child of that parent is indented by this amount
// (plus a small gap before the sub-flag's own controls), so the child
// content sits well to the right of the parent toggle. The vertical
// tree line itself is drawn once on the parent's *children container*
// (not per child row), so the line is continuous across the gaps
// between siblings instead of showing as disconnected segments.
const PARENT_TOGGLE_WIDTH_PX = 58;
// Default horizontal position of the 2px spine within the children
// container. Chosen so the line's center is directly under the
// (single-mode) parent toggle's center (x=29 = PARENT_TOGGLE_WIDTH_PX
// / 2). In multi mode the parent toggle is wider, so we re-measure the
// rendered FlagControl and recenter the rail accordingly (see
// `useToggleColumnCenter` below).
const CHILDREN_RAIL_LEFT_PX = PARENT_TOGGLE_WIDTH_PX / 2 - 1;
const RAIL_WIDTH_PX = 2;

/**
 * Tracks the rendered width of the parent's toggle column and returns
 * the x-coordinate (in the children container's coordinate system) at
 * which a 2px-wide vertical rail should be drawn so it sits exactly
 * under the toggle's horizontal center.
 *
 * Until the first measurement completes (e.g. on the very first render
 * before layout, or in jsdom where `offsetWidth` returns 0), we fall
 * back to `CHILDREN_RAIL_LEFT_PX` so single-mode rendering is correct
 * out of the gate without needing a layout effect.
 */
function useToggleColumnCenter(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [leftPx, setLeftPx] = useState<number>(CHILDREN_RAIL_LEFT_PX);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      // `offsetWidth` is 0 in jsdom and during the very first paint
      // for some components — keep the default in that case rather
      // than collapse the rail to x=-1.
      if (w <= 0) return;
      setLeftPx(Math.max(0, Math.round(w / 2 - RAIL_WIDTH_PX / 2)));
    };
    update();
    // ResizeObserver is missing in jsdom (test env) and older browsers.
    // Skip the observer when unavailable — the one-shot measurement
    // above is enough for static layouts, and tests don't assert on
    // the rail position anyway.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled]);

  return { ref, leftPx };
}

function FlagRow({ node, depth = 0, ...panel }: FlagRowProps) {
  const meta = getFeatureFlagMeta(node.key);
  const hasChildren = node.children.length > 0;
  const [expanded, toggleExpanded] = useFlagExpanded(node.key);
  // Keep the children rail centered under the parent's toggle column,
  // even though the toggle's width changes between modes (Switch in
  // single, On/Off + revert sandwich in multi).
  const { ref: toggleColumnRef, leftPx: railLeftPx } = useToggleColumnCenter(hasChildren);
  const enabledChildKeys = useMemo(
    () =>
      hasChildren
        ? node.children
            .map((c) => c.key)
            .filter((k) => isFlagEffectivelyOn(k, panel.currentStates, panel.pendingChanges))
        : [],
    [hasChildren, node.children, panel.currentStates, panel.pendingChanges]
  );

  // Clicking anywhere on the label/description area (but not on the
  // toggle itself, which is a sibling) expands or collapses the section.
  // Sub-flag rows (`hasChildren === false`) have no expand action, so we
  // only attach the handler when it'd actually do something.
  const labelBoxClickProps = hasChildren
    ? {
        onClick: toggleExpanded,
        sx: {
          cursor: "pointer",
          userSelect: "none" as const,
        },
        role: "button" as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        },
      }
    : {};

  const rowContent = (
    <>
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Wrap FlagControl so we can measure its rendered width and
            position the children rail under its horizontal center.
            `inline-flex` keeps the wrapper sized to the control. */}
        <Box ref={toggleColumnRef} sx={{ display: "inline-flex" }}>
          <FlagControl flagKey={node.key} depth={depth} {...panel} />
        </Box>
        <Box flex={1} minWidth={0} {...labelBoxClickProps}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body1" fontWeight={depth === 0 ? 600 : 400}>
              {meta.label}
            </Typography>
            <FlagChips meta={meta} />
            {hasChildren && !expanded && <CollapsedAddOnsBadge enabledKeys={enabledChildKeys} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            {meta.description}
          </Typography>
          <DependencyHint flagKey={node.key} currentStates={panel.currentStates} />
        </Box>
        {hasChildren && (
          <IconButton
            size="small"
            aria-label={
              expanded ? `Collapse ${meta.label} sub-flags` : `Expand ${meta.label} sub-flags`
            }
            aria-expanded={expanded}
            onClick={toggleExpanded}
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 150ms ease",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {hasChildren && (
        <Collapse in={expanded} unmountOnExit>
          {/* Single continuous spine behind the entire children stack.
              Because the spine is drawn by the children container and
              not by each row, the 12px Stack gaps don't break the
              line. Spine span is the full height of this Box, which
              includes those gaps. */}
          <Box
            sx={{
              position: "relative",
              mt: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                left: `${railLeftPx}px`,
                top: 0,
                bottom: 0,
                width: `${RAIL_WIDTH_PX}px`,
                backgroundColor: (theme) => theme.palette.divider,
              },
            }}
          >
            <Stack spacing={1.5}>
              {node.children.map((child) => (
                <FlagRow key={child.key} node={child} depth={depth + 1} {...panel} />
              ))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </>
  );

  if (depth === 0) {
    return <Box>{rowContent}</Box>;
  }

  // Indent sub-flag content to line up with the parent's content (58px
  // for the toggle column + 16px gap, matching the depth-0 Stack's
  // `spacing={2}`). The spine is drawn by our parent's children
  // container, so no per-row rail is needed here.
  return <Box sx={{ pl: `${PARENT_TOGGLE_WIDTH_PX + 16}px` }}>{rowContent}</Box>;
}

// MUI's `size="small"` chip renders at 24px tall with 13px text, which is
// a bit heavy when stacked inline next to flag labels. Shrink both while
// keeping the outlined silhouette so they still read as categorical tags.
//
// Subtlety: the font size MUST be set on the `.MuiChip-label` selector
// rather than the chip root — MUI applies its own `fontSize` to the
// label via the `size` variant, which would otherwise win over a root
// sx declaration. With the correct font size, `lineHeight: 1` lets the
// flex-centered label track the chip's 20px height exactly.
const COMPACT_CHIP_SX = {
  height: 20,
  "& .MuiChip-label": {
    fontSize: "0.6875rem", // 11px
    lineHeight: 1,
    px: 0.75,
  },
} as const;

// Tooltip-only indicators (staff-only icon, "in development" / "default
// on" chips, and the `+N` add-ons badge) are not interactive — they
// surface info on hover. Use `cursor: "help"` consistently across all of
// them so hover behavior reads as "this is informational" rather than
// inviting a click. Mirrors the existing cursor on the `+N` badge.
const INFORMATIONAL_INDICATOR_SX = {
  width: 18,
  height: 18,
  display: "block",
  cursor: "help",
} as const;

const COMPACT_CHIP_INFORMATIONAL_SX = {
  ...COMPACT_CHIP_SX,
  cursor: "help",
} as const;

/**
 * Resolve a flag's effective on/off state with pending changes layered on
 * top of `currentStates`. Multi-tenant "mixed" is treated as not-strictly-
 * on (matching how dependency hints and the `+N` add-on count behave —
 * both want a conservative "definitely enabled" answer).
 */
export function isFlagEffectivelyOn(
  key: FeatureFlagKey,
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>,
  pendingChanges: Partial<Record<FeatureFlagKey, PendingValue>> | undefined
): boolean {
  const pending = pendingChanges?.[key];
  if (pending !== undefined) return pending === true;
  return currentStates[key] === true;
}

/**
 * Tooltip body shared between the staff list page chip and the toggle
 * panel's collapsed-module badge — both surfaces need to read identically
 * so users learn the format once. When `enabledKeys` is empty we fall
 * back to the caller-supplied empty text so we don't show a header with
 * nothing under it.
 */
export function EnabledFlagsTooltipBody({
  enabledKeys,
  header,
  emptyText,
  lead,
}: {
  enabledKeys: readonly FeatureFlagKey[];
  header: string;
  emptyText: string;
  /**
   * Optional headline rendered above `header`, separated by a small
   * vertical gap. Used by module chips on the staff feature-flag grid
   * to clarify that the module itself is on (e.g. "Waste enabled")
   * before listing the enabled add-ons under it.
   */
  lead?: string;
}) {
  if (enabledKeys.length === 0) return <>{emptyText}</>;
  return (
    <Box>
      {lead && (
        <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
          {lead}
        </Typography>
      )}
      <Typography variant="caption" fontWeight={600} display="block">
        {header}
      </Typography>
      {enabledKeys.map((k) => (
        <Typography key={k} variant="caption" display="block">
          {getFeatureFlagMeta(k).label}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * "+N" affordance shown beside a module's chips when its accordion is
 * collapsed, so staff can see at a glance how many add-ons are enabled
 * without expanding the section. Hovering the badge reveals the same
 * tooltip the list-page chip shows.
 *
 * Renders nothing when no add-ons are enabled — a "+0" badge would just
 * add visual noise to a row that's already telling the user "module on,
 * nothing else". Mirrors the list-page chip behavior.
 */
function CollapsedAddOnsBadge({ enabledKeys }: { enabledKeys: FeatureFlagKey[] }) {
  if (enabledKeys.length === 0) return null;
  return (
    <Tooltip
      title={
        <EnabledFlagsTooltipBody
          enabledKeys={enabledKeys}
          header="Add-ons enabled:"
          emptyText="No add-ons enabled"
        />
      }
      arrow
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          color: "encampBlue.main",
          fontWeight: 600,
          cursor: "help",
          lineHeight: 1,
        }}
      >
        +{enabledKeys.length}
      </Typography>
    </Tooltip>
  );
}

function FlagChips({ meta }: { meta: FeatureFlagMeta }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" useFlexGap>
      {meta.staffOverride && (
        <Tooltip title="This feature is always visible for Staff users regardless of the feature flag">
          {/* Icon-only affordance using the Encamp logo so the staff-only
              indicator is visually consistent with the brand mark in the
              left nav. The tooltip carries the full explanation. */}
          <Box
            component="img"
            src={EncampLogo}
            alt="Always visible for staff"
            aria-label="Always visible for staff"
            sx={INFORMATIONAL_INDICATOR_SX}
          />
        </Tooltip>
      )}
      {meta.inDevelopment && (
        <Tooltip title="This flag is still under active development.">
          <Chip
            size="small"
            label="in development"
            color="info"
            variant="outlined"
            sx={COMPACT_CHIP_INFORMATIONAL_SX}
          />
        </Tooltip>
      )}
      {meta.defaultValue && (
        <Tooltip title="Enabled by default for tenants without an explicit setting.">
          <Chip
            size="small"
            label="default on"
            color="success"
            variant="outlined"
            sx={COMPACT_CHIP_INFORMATIONAL_SX}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

function DependencyHint({
  flagKey,
  currentStates,
}: {
  flagKey: FeatureFlagKey;
  currentStates: Partial<Record<FeatureFlagKey, FlagState>>;
}) {
  const meta = getFeatureFlagMeta(flagKey);

  const labels = (keys: readonly FeatureFlagKey[]) =>
    keys.map((k) => getFeatureFlagMeta(k).label).join(", ");

  const thisFlagOn = currentStates[flagKey] === true || currentStates[flagKey] === "mixed";

  if (thisFlagOn) {
    // Both the implicit parent dependency and explicit `requires` need
    // to be on for this flag to be functional for the customer. Treat
    // them uniformly here so a child flag whose parent is off renders
    // the same kind of warning as an unmet `requires`.
    const allDeps: FeatureFlagKey[] = [
      ...(meta.parentKey ? [meta.parentKey] : []),
      ...meta.requires,
    ];
    if (allDeps.length === 0) return null;
    // In multi mode `currentStates[k]` may be "mixed"; treat anything that
    // isn't strictly `true` as "not guaranteed on" so we don't falsely
    // claim the dependency is satisfied across a mixed selection.
    const unmet = allDeps.filter((k) => currentStates[k] !== true);
    if (unmet.length === 0) return null;

    // A `staffOverride: true` dep is on for staff regardless of the
    // stored customer value, so for staff it's effectively satisfied.
    // Partition unmet deps so we can show "active for staff only" when
    // every unmet dep is staff-overridden, vs. the stronger "inactive"
    // when at least one dep is genuinely off.
    const trulyOff = unmet.filter((k) => !getFeatureFlagMeta(k).staffOverride);
    const staffOnlyMissing = unmet.filter((k) => getFeatureFlagMeta(k).staffOverride);

    if (trulyOff.length > 0) {
      return (
        <Box mt={0.5}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Inactive — needs ${labels(unmet)}`}
            sx={COMPACT_CHIP_SX}
          />
        </Box>
      );
    }

    // Staff orange (MUI `warning`) signals "this is a staff-only
    // affordance" consistently with the rest of the staff UI (left
    // nav, staff override icon, etc.). Same hue as the "Inactive"
    // chip above — the labels are different enough that the shared
    // color works as a "this row needs your attention" signal.
    return (
      <Box mt={0.5}>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`Active for staff only — customer needs ${labels(staffOnlyMissing)}`}
          sx={COMPACT_CHIP_SX}
        />
      </Box>
    );
  }

  // Flag is off. Show informational "Requires: …" only for explicit
  // `requires` — the parent relationship is already conveyed by the
  // indented sub-flag tree, so repeating it as a caption would just be
  // noise on every add-on row.
  if (meta.requires.length === 0) return null;
  return (
    <Typography variant="caption" display="block" sx={{ mt: 0.25, color: "info.dark" }}>
      Requires: {labels(meta.requires)}
    </Typography>
  );
}

type FlagControlProps = FeatureFlagTogglePanelProps & {
  flagKey: FeatureFlagKey;
  depth: number;
};

function FlagControl({
  mode,
  currentStates,
  pendingChanges,
  onChange,
  disabled,
  flagKey,
  depth,
}: FlagControlProps) {
  const current = currentStates[flagKey] ?? false;
  const pending = pendingChanges?.[flagKey];
  const effective: FlagState = pending === undefined ? current : pending;
  const controlSize = depth > 0 ? "small" : "medium";

  if (mode === "single") {
    return (
      <Switch
        size={controlSize}
        checked={effective === true}
        disabled={disabled}
        onChange={(_e, checked) => onChange(flagKey, checked)}
      />
    );
  }

  // When the selected tenants currently differ on this flag (`current
  // === "mixed"`), neither On nor Off is the "original" value, so a
  // double-click can't be used to revert. We instead surface a small
  // explicit Revert icon to the left of the toggle whenever there's a
  // pending change to clear. For homogeneous selections, clicking the
  // already-current value still revertspending (handled below) — that
  // affordance is unchanged.
  const isMixedOriginal = current === "mixed";
  const hasPendingChange = pending !== undefined;

  const handleChange = (_e: unknown, next: "on" | "off" | null) => {
    if (next == null) return;
    const nextBool = next === "on";
    // Clicking the original (homogeneous) value clears the pending
    // entry instead of recording a no-op. For mixed selections there
    // is no "original" boolean, so we always record the pending value.
    if (!isMixedOriginal && nextBool === current) {
      onChange(flagKey, undefined);
      return;
    }
    onChange(flagKey, nextBool);
  };

  const handleRevert = () => onChange(flagKey, undefined);

  // `null` keeps the ToggleButtonGroup unselected — used when the
  // selection is mixed and we have no pending value yet.
  const value: "on" | "off" | null = effective === "mixed" ? null : effective ? "on" : "off";
  const showRevert = isMixedOriginal && hasPendingChange;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {/* Reserve a fixed-width slot for the Revert affordance on the
          left so rows don't shift horizontally as pending changes
          appear/disappear. When there's nothing to revert, we render
          an inert IconButton-shaped placeholder so the slot keeps its
          dimensions; we deliberately *don't* mount the Tooltip in that
          case because MUI's Popper warns when its anchor element has
          `visibility: hidden`, which spams the test logs and slows
          rerenders enough to push the bulk-toggle test past its 5s
          jsdom timeout. */}
      <Box
        sx={{
          visibility: showRevert ? "visible" : "hidden",
          display: "inline-flex",
        }}
        aria-hidden={!showRevert}
      >
        {showRevert ? (
          <Tooltip title="Don’t change this flag — leave each selected tenant at its current value.">
            <IconButton
              size="small"
              onClick={handleRevert}
              disabled={disabled}
              aria-label={`Revert pending change for ${flagKey}`}
            >
              <Replay fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton size="small" tabIndex={-1} disableRipple aria-hidden>
            <Replay fontSize="small" />
          </IconButton>
        )}
      </Box>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        disabled={disabled}
        onChange={handleChange}
        aria-label={`Toggle ${flagKey}`}
        sx={
          depth > 0
            ? { "& .MuiToggleButton-root": { py: 0.25, px: 1, fontSize: "0.75rem" } }
            : undefined
        }
      >
        {/* Only the "On" button carries an info color — MUI's
            `ToggleButton.color` drives both the selected text color and
            the background tint, so this makes the active "On" state read
            as blue to mirror the single-mode Switch's default tint. The
            "Off" button stays neutral by default; there's no standalone
            "selected" state we need to style for it. */}
        <ToggleButton value="on" color="info" aria-label="Turn on for all selected">
          On
        </ToggleButton>
        <ToggleButton value="off" aria-label="Turn off for all selected">
          Off
        </ToggleButton>
      </ToggleButtonGroup>
      {/* Mirror the revert slot on the right with an invisible
          IconButton-shaped placeholder. This keeps the toggle group
          visually centered within the parent's toggle column so the
          children-rail line stays under the toggle's center. The DOM
          structure mirrors the left slot so widths stay in lockstep
          even if MUI's IconButton sizing changes. */}
      <Box sx={{ visibility: "hidden", display: "inline-flex" }} aria-hidden>
        <IconButton size="small" tabIndex={-1} disableRipple>
          <Replay fontSize="small" />
        </IconButton>
      </Box>
    </Stack>
  );
}

/**
 * Small helper so callers can render a consistent "N pending changes" hint
 * without re-implementing the counting logic.
 */
export function countPendingChanges(
  pendingChanges: Partial<Record<FeatureFlagKey, PendingValue>>
): number {
  return Object.values(pendingChanges).filter((v) => v !== undefined).length;
}

export function PendingChangesHint({
  pendingChanges,
}: {
  pendingChanges: Partial<Record<FeatureFlagKey, PendingValue>>;
}) {
  const count = countPendingChanges(pendingChanges);
  // Empty state intentionally renders nothing — the parent surface (the
  // bulk drawer subtitle) already explains the no-op default behavior,
  // so a duplicate "Only flags you change…" alert is just visual noise.
  if (count === 0) return null;
  return (
    <Alert severity="success" variant="outlined">
      <strong>{count}</strong> {count === 1 ? "flag" : "flags"} pending change.
    </Alert>
  );
}
